// Prepara lo que Chrome va oyendo antes de buscar el comando (partida y repeticion).
// Solo trabaja sobre el texto: no abre ni cierra la escucha.
// 1) palabras parecidas -> la palabra del vocabulario (isquierda -> izquierda, meno -> menos);
// 2) solo cuenta lo ultimo: desde la ultima palabra clave (mueve, incorpora, captura,
//    domina, Xima, desde) o, si no hay, las ultimas palabras que aun no se usaron;
// 3) «jugador 2», «segundo jugador», «cara de jugador 3» -> cara de ese jugador.

export const VOICE_COMMAND_WINDOW_WORDS = 6;

// ===================== TABLA CENTRAL DE PALABRAS DE COMANDO =====================
// Palabra del comando -> variantes conocidas de como la escribe Chrome (sin tildes).
// Ademas de las variantes, una palabra oida activa el comando si:
//   (a) empieza por las 4 primeras letras del comando,
//   (b) contiene 4 o mas letras seguidas del comando y se le parece,
//   (c) se le parece letra a letra tras igualar sonidos (s/z/c, b/v, ll/y, h muda, qu/k...).
// Las palabras cortas (STRICT_WORDS) solo por variantes o por (c) con la misma primera letra.
// Prefiere ejecutar de mas: si algo se dispara sin querer, se anade a VOICE_WORD_EXCLUSIONS.
export const VOICE_COMMAND_WORD_TABLE = {
  izquierda: ["isquierda", "iskierda", "izkierda", "esquierda", "izquieda", "izquierd", "quierda", "izquierdas", "izquierdo", "isquierdo", "esquierdo"],
  derecha: ["derech", "dereche", "derechas", "dereca", "derecho"],
  arriba: ["ariba", "arrib", "arribas", "aribba"],
  abajo: ["avajo", "abaj", "abajos"],
  sube: ["subele", "subi", "suba", "subelo", "sobe", "suve", "subir"],
  baja: ["bajale", "baje", "vaja", "bajalo", "vaje", "bajar"],
  menos: ["meno", "menus", "venus", "menoz", "menso"],
  mas: ["mas"],
  aumenta: ["aumentar", "aumente", "aumentale", "aumentame"],
  disminuye: ["disminuir", "disminuya", "diminuye", "disminuyes", "disminulle", "disminuie", "diminuir", "disminuyo"],
  acercate: ["acercar", "acerca", "acercame"],
  alejate: ["alejar", "aleja", "alejame"],
  gira: ["giro", "girar", "jira", "gire"],
  jugador: ["jugadora", "jugadores"],
  especial: ["espesial", "expecial", "especiales"],
  enfoca: ["enfocar", "enfoque", "enfocame"],
  planta: ["plantas"],
  superior: ["superio"],
  cenital: ["senital", "zenital"],
  siguiente: ["siguente", "sigiente", "siguient", "siguiete"],
  anterior: ["anterio"],
  atras: ["atra", "atraz"],
  // «para» sola o al final de la frase tambien pausa (ver replayVoiceCommands).
  pausa: ["pauza", "pausar", "pare", "parale", "paralo", "detente", "deten", "detener", "detenga", "estop", "stop", "alto", "basta"],
  play: ["plei", "plai", "pley"],
  reiniciar: ["reinicia", "reinicio"],
  velocidad: ["belocidad"],
  mueve: ["muevete", "mueva", "muevan", "mover", "muevo", "move", "muebe"],
  incorpora: ["incorpore", "incorporar", "incorporo", "corpora", "incorporen", "incorporalo", "incoorpora", "incoopora"],
  captura: ["capturar", "capture", "capturo", "captur", "capturen"],
  domina: ["dominar", "domine", "domino", "dominen"],
  xima: ["cima", "sima", "chima", "zima", "jima"],
  desde: ["dezde"],
  // Numeros y letras del tablero (solo por variantes).
  cinco: ["sinco", "cinko"],
  seis: ["sei", "seys", "sies"],
  siete: ["sete"],
  doce: ["dose"],
  trece: ["trese"],
  once: ["onse"],
  diez: ["dies"],
  nueve: ["nuebe"],
  hache: ["ache"],
  ge: ["je"],
  ka: ["ca"],
};

// Palabras que NO deben activar ningun comando (falsos positivos). Editar aqui.
export const VOICE_WORD_EXCLUSIONS = new Set([
  "quiero", "quieres", "quiere", "quiera", "quieras", "quieren",
  "buenos", "buenas", "bueno", "echa", "echar", "hecha", "hecho", "fecha", "techo",
  "trabajo", "trabaja", "cuenta", "cuento", "cuentas", "momento", "comenta",
  "espera", "esperar", "espero", "esperen", "especie", "plano", "planeta", "plantea", "planes",
  "jugada", "jugadas", "jugado", "jugando", "juega", "jugar", "juegan", "juego", "juegos",
  "llueve", "domingo", "vaya", "valla", "sabe", "sabes", "causa", "pasa", "pase", "paso",
  "entra", "entrar", "arriesga", "derechos", "abajos", "aumento", "capital", "esperate",
  "partida", "partido", "masa", "misma", "mismo", "mesa",
]);

// Solo variantes o parecido con la misma primera letra (palabras cortas o delicadas).
const STRICT_WORDS = new Set(["mas", "gira", "sube", "baja", "play", "xima", "atras", "desde"]);
// Numeros y letras del tablero: solo por su lista de variantes (nunca por parecido).
const VARIANT_ONLY_WORDS = new Set(["cinco", "seis", "siete", "doce", "trece", "once", "diez", "nueve", "hache", "ge", "ka"]);

const VARIANT_TO_WORD = Object.entries(VOICE_COMMAND_WORD_TABLE).reduce((map, [word, variants]) => {
  map[word] = word;
  variants.forEach((variant) => {
    map[variant] = word;
  });
  return map;
}, {});

const editDistance = (left, right) => {
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let row = 1; row <= left.length; row += 1) {
    let diagonal = previous[0];
    previous[0] = row;
    for (let column = 1; column <= right.length; column += 1) {
      const saved = previous[column];
      previous[column] = Math.min(
        previous[column] + 1,
        previous[column - 1] + 1,
        diagonal + (left[row - 1] === right[column - 1] ? 0 : 1)
      );
      diagonal = saved;
    }
  }
  return previous[right.length];
};

// Iguala sonidos del español: h muda, z/c(e,i)->s, qu/c(a,o,u)/k->k, v->b, ll->y, g(e,i)->j, letras dobles.
export const phoneticVoiceWord = (word) =>
  String(word || "")
    .replace(/ch/g, "§")
    .replace(/h/g, "")
    .replace(/qu/g, "k")
    .replace(/c([ei])/g, "s$1")
    .replace(/c/g, "k")
    .replace(/z/g, "s")
    .replace(/v/g, "b")
    .replace(/ll/g, "y")
    .replace(/g([ei])/g, "j$1")
    .replace(/x/g, "s")
    .replace(/(.)\1+/g, "$1")
    .replace(/§/g, "ch");

const editLimitFor = (commandWord) => (commandWord.length >= 9 ? 3 : commandWord.length >= 6 ? 2 : 1);

const longestSharedRun = (left, right) => {
  let best = 0;
  for (let i = 0; i < left.length; i += 1) {
    for (let j = 0; j < right.length; j += 1) {
      let run = 0;
      while (left[i + run] && left[i + run] === right[j + run]) run += 1;
      if (run > best) best = run;
    }
  }
  return best;
};

const COMMAND_WORDS = Object.keys(VOICE_COMMAND_WORD_TABLE)
  .filter((word) => !VARIANT_ONLY_WORDS.has(word))
  .map((word) => ({ word, phonetic: phoneticVoiceWord(word) }));

/** Palabra del comando que activa una palabra oida, o null. */
export const matchVoiceCommandWord = (heard) => {
  const word = String(heard || "");
  if (!word || VOICE_WORD_EXCLUSIONS.has(word)) return null;
  if (VARIANT_TO_WORD[word]) return VARIANT_TO_WORD[word];
  if (word.length < 3 || /\d/.test(word) || isNumberWord(word) || isFileLetter(word)) return null;

  const heardPhonetic = phoneticVoiceWord(word);
  let best = null;
  let bestScore = Infinity;
  COMMAND_WORDS.forEach(({ word: commandWord, phonetic }) => {
    const distance = editDistance(heardPhonetic, phonetic);
    let score = Infinity;
    if (STRICT_WORDS.has(commandWord)) {
      if (distance <= 1 && heardPhonetic[0] === phonetic[0] && word.length >= 3) score = distance;
    } else {
      if (distance <= editLimitFor(commandWord)) score = distance;
      if (word.length >= 4 && phonetic.length >= 4 && heardPhonetic.startsWith(phonetic.slice(0, 4))) {
        score = Math.min(score, 0.5 + distance / 100);
      }
      if (
        word.length >= 4 &&
        longestSharedRun(heardPhonetic, phonetic) >= 4 &&
        distance <= Math.ceil(phonetic.length / 2)
      ) {
        score = Math.min(score, 1.5 + distance / 100);
      }
    }
    if (score < bestScore || (score === bestScore && best && commandWord.length > best.length)) {
      best = commandWord;
      bestScore = score;
    }
  });
  return best;
};

export const fuzzyVoiceWord = (word) => matchVoiceCommandWord(word) || word;

const isNumberWord = (word) =>
  /^(?:\d+|uno|un|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|once|doce|trece)$/.test(word || "");
const isFileLetter = (word) =>
  /^(?:[a-m]|be|ve|ce|de|efe|ge|hache|jota|ka|ele|eme)$/.test(word || "");

// «se» junto a una casilla: delante de un numero es la letra C; detras de una letra es 6.
const fixCellWords = (words) =>
  words.map((word, index) => {
    if (word !== "se") return word;
    if (isNumberWord(words[index + 1])) return "ce";
    if (isFileLetter(words[index - 1])) return "seis";
    return word;
  });

const JOINABLE_WORDS = new Set(["disminuye", "izquierda", "derecha", "aumenta", "siguiente", "acercate", "alejate", "incorpora"]);

// Une trozos de una palabra partida («dis mi nuye» -> «disminuye»).
const joinSplitWords = (words) => {
  const out = [];
  for (let index = 0; index < words.length; index += 1) {
    let joined = null;
    for (let size = 3; size >= 2 && !joined; size -= 1) {
      if (index + size > words.length) continue;
      const chunk = words.slice(index, index + size);
      if (chunk.some((part) => part.length > 5 || /\d/.test(part))) continue;
      const candidate = chunk.join("");
      if (candidate.length < 7) continue;
      const target = matchVoiceCommandWord(candidate);
      if (target && JOINABLE_WORDS.has(target)) joined = { word: target, size };
    }
    if (joined) {
      out.push(joined.word);
      index += joined.size - 1;
    } else {
      out.push(words[index]);
    }
  }
  return out;
};

export const canonicalVoiceWords = (normalizedText) =>
  fixCellWords(
    joinSplitWords(
      String(normalizedText || "")
        .split(" ")
        .filter(Boolean)
    ).map(fuzzyVoiceWord)
  );

// Diagnostico: que palabra oida se cambio por que palabra de comando («isquierda»→izquierda).
export const describeVoiceWordChanges = (normalizedText) =>
  String(normalizedText || "")
    .split(" ")
    .filter(Boolean)
    .map((word) => [word, fuzzyVoiceWord(word)])
    .filter(([word, mapped]) => word !== mapped)
    .map(([word, mapped]) => `«${word}»→${mapped}`)
    .join(", ");

const ORDINAL_NUMBERS = {
  1: 1, uno: 1, un: 1, una: 1, primer: 1, primero: 1, primera: 1,
  2: 2, dos: 2, segundo: 2, segunda: 2,
  3: 3, tres: 3, tercer: 3, tercero: 3, tercera: 3,
  4: 4, cuatro: 4, cuarto: 4, cuarta: 4,
};

// Numero que va justo antes o despues de una palabra («jugador 2», «segundo jugador»).
const numberNear = (words, noun, max) => {
  for (let index = words.length - 1; index >= 0; index -= 1) {
    if (words[index] !== noun) continue;
    const after = ORDINAL_NUMBERS[words[index + 1]];
    if (after && after <= max) return after;
    const before = ORDINAL_NUMBERS[words[index - 1]];
    if (before && before <= max && !/^\d+$/.test(words[index - 1])) return before;
  }
  return null;
};

// «jugador 2», «jugador dos», «segundo jugador», «enfoca a jugador 3», «cara de jugador 4».
export const parsePlayerFaceWords = (words) => numberNear(words, "jugador", 4);

// Comandos de camara que se deciden por palabras (partida y repeticion):
// «enfoca el bloque especial [del jugador 2]», «primera/segunda/tercera planta», «la cima».
export const parseCameraFocusWords = (words) => {
  const has = (word) => words.includes(word);
  if (has("especial") && (has("enfoca") || has("enfocar") || has("bloque") || words.length <= 2)) {
    const player = parsePlayerFaceWords(words);
    return player
      ? { id: `focus_special:${player}`, label: `Especial del jugador ${player}` }
      : { id: "focus_special", label: "Enfocar bloque especial" };
  }
  const text = ` ${words.join(" ")} `;
  if (/ (?:vista superior|vista de arriba|vista desde arriba|desde arriba|cenital|vista cenital) /.test(text)) {
    return { id: "top_view", label: "Vista superior" };
  }
  const floor = numberNear(words, "planta", 3) || numberNear(words, "piso", 3) || numberNear(words, "nivel", 3);
  if (floor) return { id: `focus_floor:${floor}`, label: `Planta ${floor}` };
  if ((has("enfoca") || has("enfocar") || has("ve")) && has("xima")) {
    return { id: "focus_floor:4", label: "La cima" };
  }
  return null;
};

// «seguir camara» / «activa el seguimiento» -> follow_on; «camara libre» / «deten el seguimiento»
// / «deja de seguir» -> follow_off. Va antes que «pausa» y «siguiente». Usa las palabras
// tal como llegan (antes de cambiarlas por las del comando).
const FOLLOW_OFF_WORDS = ["deten", "detener", "detente", "deja", "dejar", "desactiva", "desactivar", "para", "parar",
  "quita", "quitar", "apaga", "apagar", "sin", "no", "pausa", "detenga"];
export const parseCameraFollowWords = (rawWords) => {
  const has = (word) => rawWords.includes(word);
  const camara = has("camara") || has("camaras");
  const libre = has("libre");
  const follow = has("seguimiento") || ["seguir", "sigue", "siga", "sigan", "seguirla", "siguiendo"].some(has);
  if (camara && libre) return { id: "follow_off", label: "Cámara libre" };
  if (!has("seguimiento") && !(follow && camara) && !(has("deja") && has("seguir"))) return null;
  const off = FOLLOW_OFF_WORDS.some(has);
  return off ? { id: "follow_off", label: "Cámara libre" } : { id: "follow_on", label: "Seguir cámara" };
};

const ANCHORS = new Set(["mueve", "incorpora", "captura", "domina", "xima", "desde"]);

/**
 * De las palabras de un resultado, saca el trozo donde buscar el comando.
 * consumedWords: palabras de ese resultado ya usadas por un comando anterior.
 * Devuelve { text, anchor, words } o null si no queda nada.
 */
export const extractVoiceCommandSegment = (words, consumedWords = 0) => {
  const tail = words.slice(Math.min(Math.max(0, consumedWords), words.length));
  if (!tail.length) return null;

  // «enfoca la cima»: aqui «cima» no es la palabra clave Xima.
  const focusWords = parseCameraFocusWords(tail.slice(-VOICE_COMMAND_WINDOW_WORDS));
  if (focusWords) {
    const segment = tail.slice(-VOICE_COMMAND_WINDOW_WORDS);
    return { text: segment.join(" "), anchor: "", words: segment };
  }

  let anchorIndex = -1;
  for (let index = tail.length - 1; index >= 0; index -= 1) {
    if (ANCHORS.has(tail[index])) {
      anchorIndex = index;
      break;
    }
  }

  if (anchorIndex >= 0) {
    const anchor = tail[anchorIndex];
    const rest = tail.slice(anchorIndex + 1);
    // «captura G4» y «domina G4» = mover un bloque a G4.
    if (anchor === "captura" || anchor === "domina") {
      const segment = ["mueve", ...rest];
      return { text: segment.join(" "), anchor, words: segment };
    }
    if (anchor === "xima") return rest.length ? { text: rest.join(" "), anchor, words: rest } : null;
    const segment = tail.slice(anchorIndex);
    return { text: segment.join(" "), anchor, words: segment };
  }

  const segment = tail.slice(-VOICE_COMMAND_WINDOW_WORDS);
  return { text: segment.join(" "), anchor: "", words: segment };
};
